<template>
    <div class="vx-file-upload" ref="loader">
        <label v-if="label" class="vs-select--label">{{ label }}</label>
        <vx-scroll-area>
            <div
                @dragover.prevent="dragOver"
                @dragleave.prevent="dragLeave"
                @drop.prevent="receiveFileDrop($event)"
            >
                <div :class="{ hidden: !hovering }" class="overlay-wrapper">
                    <slot name="dropzone" :canDrop="canReceive">
                        <div v-if="canReceive" class="drag-overlay"></div>
                        <div v-else class="drag-overlay error">
                            <strong style="margin: 5px">{{ this.localMaxText }}</strong>
                        </div>
                    </slot>
                </div>

                <vs-upload
                    ref="uploader"
                    class="vs-upload"
                    :show-upload-button="true"
                    :automatic="false"
                    :text="text"
                    :multiple="limit == null || limit > 1"
                    :limit="limit"
                    @update:vsFile="onReceiveConfirmed"
                />
            </div>
        </vx-scroll-area>
    </div>
</template>

<script>
export default {
    name: 'vx-file-upload',
    props: {
        value: {
            type: Array,
            default: () => []
        },
        text: {
            type: String,
            default: 'Upload a File'
        },
        maxText: {
            type: String,
            default: 'Upload a File'
        },
        limit: {
            type: [Number, String],
            default: null
        },
        label: {
            type: String,
            default: null
        },
        allowTypes: {
            type: [String, Array],
            default: () => []
        }
    },
    data() {
        return {
            files: [],
            srcs: [],
            removals: [],
            hovering: false,
            loadCheck: null,
            requestCount: 0,
            initialized: false
        }
    },
    computed: {
        localfiles: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            }
        },
        localMaxText() {
            if (this.maxText) {
                this.maxText
            }
            return `Restricted to ${this.limit} files`
        },
        loading_file() {
            return this.srcs.length !== this.files.length
        },
        canReceive() {
            if (this.limit == null) {
                return true
            }
            return this.localfiles.length + this.requestCount <= this.limit
        },
        types() {
            if (!this.allowTypes) {
                return []
            } else if (typeof this.allowTypes === 'string') {
                return [this.allowTypes]
            }
            return this.allowTypes
        }
    },
    methods: {
        dragOver(event) {
            this.hovering = true
            var files = 0
            event.dataTransfer.items.forEach((f) => {
                if (f.kind === 'file') {
                    files++
                }
            })
            this.requestCount = files
        },
        dragLeave(event) {
            this.hovering = false
            this.requestCount = 0
        },
        setLoadCheck() {
            if (!this.loading_file) {
                if (this.loadCheck) {
                    clearTimeout(this.loadCheck)
                }
                this.loadCheck = setTimeout(
                    function () {
                        if (!this.loading_file) {
                            this.endLoad(this.$refs.loader)
                        }
                    }.bind(this),
                    200
                )
            }
        },
        onReceiveConfirmed(e) {
            this.startLoad(this.$refs.loader)
        },
        parseFiles() {
            var index = Math.min(this.files.length, this.srcs.length)
            var result = []

            for (var i = 0; i < index; i++) {
                if (!this.removals.includes(i)) {
                    var file = this.files[i]

                    result.push({
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        base64: this.srcs[i].src,
                        file: file
                    })
                }
            }
            this.localfiles = result
        },
        async receiveFileDrop(event) {
            this.hovering = false
            if (event.dataTransfer.files[0]) {
                if (!this.canReceive) {
                    this.requestCount = 0
                    return
                }

                var i = 0
                while (event.dataTransfer.files[i]) {
                    if (!this.checkType(event.dataTransfer.files[i])) {
                        this.notifyError(
                            'Invalid Type',
                            `Uploader only accepts ${this.types.textualize(
                                'or'
                            )} files. Cannot upload: ${event.dataTransfer.files[i].name}.`,
                            5000
                        )
                        this.requestCount = 0
                        return
                    }
                    i++
                }

                var fakeEvent = {
                    target: {
                        value: event.dataTransfer,
                        files: event.dataTransfer.files
                    }
                }

                this.$refs.uploader.getFiles(fakeEvent)
                this.requestCount = 0
            }
        },
        checkType(file) {
            if (this.types.any()) {
                return this.types.any((t) => {
                    return (
                        file.type && (file.type.startsWith(`${t}/`) || file.type.endsWith(`/${t}`))
                    )
                })
            }
            return true
        }
    },
    mounted() {
        if (!this.initialized) {
            // MASSIVE HACK! Still awesome though!
            this.$watch('$refs.uploader.itemRemove', (new_value, old_value) => {
                this.removals = new_value
                this.parseFiles()
            })
            this.$watch('$refs.uploader.srcs', (new_value, old_value) => {
                this.srcs = new_value
                this.setLoadCheck()
                this.parseFiles()
            })
            this.$watch('$refs.uploader.filesx', (new_value, old_value) => {
                this.files = new_value
                this.setLoadCheck()
                this.parseFiles()
            })

            if (this.value.any()) {
                var evt = {
                    dataTransfer: {
                        value: { fakeEvent: true },
                        files: { ...this.value.map((f) => f.file) }
                    }
                }

                this.receiveFileDrop(evt)
            }
            this.initialized = true
        }
    }
}
</script>

<style scoped>
.vs-upload ::v-deep .con-img-upload {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    border: 2px dashed rgba(0, 0, 0, 0.1);
}
.hidden {
    display: none;
}
.overlay-wrapper {
    pointer-events: none;
    position: absolute;
    height: 100%;
    width: 100%;
    z-index: 10000;
}
.drag-overlay.error {
    border: 2px dashed rgba(var(--vs-danger), 0.5);
    background-color: rgba(var(--vs-danger), 0.1);
}
.drag-overlay {
    border: 2px dashed rgba(var(--vs-primary), 0.5);
    background-color: rgba(var(--vs-primary), 0.1);
    opacity: 1;
    height: 100%;
    width: 100%;
    border-radius: 10px;
}
.file-hover {
    background-color: gray;
}
.vs-upload ::v-deep .con-img-upload > .con-input-upload:hover {
    transform: scale(0.98);
}
.vs-upload ::v-deep .btn-upload-all {
    pointer-events: none;
}
.vs-upload ::v-deep .img-upload > .btn-upload-file {
    pointer-events: none;
}
.vs-upload ::v-deep .btn-upload-file > i {
    display: none;
}
</style>
